import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Users, KeyRound, ShieldOff, Copy, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/cloudinary";
import CarnetConducirForm from "@/components/CarnetConducirForm";
import ColaboradorTrabajosDialog from "@/components/ColaboradorTrabajosDialog";

const EMPTY_EXT = {
  nombre: "", nif: "", iban: "", num_seguridad_social: "", telefono: "", email: "", zona_habitual: "",
  especialidad: "", autonomo: false, tarifa_dia: "",
  forma_pago: "", dias_vencimiento: "", notas: "", activo: true,
  fecha_nacimiento: "", cumple_show: true,
  tipo_acceso: "sin_acceso",
  carnet_conducir: null,
  foto_url: "",
};

const FORMAS_PAGO = {
  transferencia: "Transferencia",
  bizum: "Bizum",
  pagare: "Pagaré",
  contado: "Contado",
};

const DIAS_AVISO_VENCIMIENTO_LABEL = "7 días";

const TIPOS_ACCESO = {
  sin_acceso: { label: "Sin acceso", badge: "bg-zinc-700/40 text-zinc-400", desc: "No puede entrar en la app" },
  habitual:   { label: "Habitual",   badge: "bg-emerald-500/20 text-emerald-300", desc: "Acceso permanente (dnf2026)" },
  puntual:    { label: "Puntual",    badge: "bg-amber-500/20 text-amber-300", desc: "Acceso temporal con vencimiento" },
};

export default function Colaboradores({ embedded = false } = {}) {
  const [externos, setExternos] = useState([]);
  const [openExt, setOpenExt] = useState(false);
  const [editExt, setEditExt] = useState(null);
  const [formExt, setFormExt] = useState(EMPTY_EXT);
  const [credDialog, setCredDialog] = useState(null);
  const [concederFor, setConcederFor] = useState(null);
  const [diasAcceso, setDiasAcceso] = useState(7);
  const [trabajosFor, setTrabajosFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/colaboradores");
    setExternos(data);
  };
  useEffect(() => { load(); }, []);

  const concederAcceso = async () => {
    try {
      const { data } = await api.post(`/colaboradores/${concederFor.id}/conceder-acceso`, { dias: diasAcceso });
      setCredDialog(data);
      setConcederFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al conceder acceso");
    }
  };

  const revocarAcceso = async (c) => {
    if (!window.confirm(`¿Revocar acceso de ${c.nombre}?`)) return;
    try {
      await api.post(`/colaboradores/${c.id}/revocar-acceso`);
      toast.success("Acceso revocado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al revocar");
    }
  };

  const saveExt = async (e) => {
    e.preventDefault();
    const p = { ...formExt };
    p.tarifa_dia = p.tarifa_dia ? parseFloat(p.tarifa_dia) : null;
    p.dias_vencimiento = p.dias_vencimiento !== "" && p.dias_vencimiento !== null
      ? parseInt(p.dias_vencimiento, 10) : null;
    p.nif = p.nif ? p.nif.trim().toUpperCase() : null;
    p.forma_pago = p.forma_pago || null;
    if (editExt) await api.patch(`/colaboradores/${editExt.id}`, p);
    else await api.post("/colaboradores", p);
    setOpenExt(false); load();
  };

  return (
    <div className={embedded ? "" : "p-8 max-w-5xl"}>
      {!embedded && (
        <PageHeader title="Colaboradores externos"
          subtitle="Montadores externos / autónomos · accesos puntuales o habituales a la app"
          action={
            <Button onClick={() => { setEditExt(null); setFormExt(EMPTY_EXT); setOpenExt(true); }}
              data-testid="new-externo-btn"
              className="bg-violet-500 hover:bg-violet-400 text-zinc-900 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Nuevo colaborador
            </Button>
          } />
      )}
      {embedded && (
        <div className="flex justify-end mb-3">
          <Button onClick={() => { setEditExt(null); setFormExt(EMPTY_EXT); setOpenExt(true); }}
            data-testid="new-externo-btn"
            className="bg-violet-500 hover:bg-violet-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Nuevo colaborador
          </Button>
        </div>
      )}

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
        <div className="text-sm font-semibold flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-violet-400" /> Colaboradores ({externos.length})
        </div>
        <div className="space-y-2" data-testid="externos-list">
          {externos.map((c) => {
            const ta = TIPOS_ACCESO[c.tipo_acceso || "sin_acceso"];
            const venc = c.user_acceso_hasta ? new Date(c.user_acceso_hasta) : null;
            const dias = venc ? Math.floor((venc - new Date()) / 86400000) : null;
            const expirado = c.tipo_acceso === "puntual" && (dias === null || dias < 0);
            return (
              <div key={c.id} data-testid={`externo-${c.id}`}
                className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm">{c.nombre}</span>
                      {c.autonomo
                        ? <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">🧑‍💼 Colaborador Autónomo</span>
                        : <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">🏢 Montador Externo</span>
                      }
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${ta.badge}`}>{ta.label}</span>
                      {c.tipo_acceso === "puntual" && venc && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          expirado ? "bg-red-500/15 text-red-300" : dias <= 2 ? "bg-orange-500/15 text-orange-300" : "bg-cyan-500/15 text-cyan-300"
                        }`}>
                          {expirado ? `Expiró hace ${-dias}d` : `${dias}d restantes`}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {c.zona_habitual || "—"}{c.telefono ? ` · ${c.telefono}` : ""}{c.tarifa_dia ? ` · ${c.tarifa_dia}€/día` : ""}
                    </div>
                    {c.user_email && (
                      <div className="text-[11px] text-zinc-500 italic mt-0.5">{c.user_email}</div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost"
                      onClick={() => setTrabajosFor(c)}
                      data-testid={`ver-trabajos-${c.id}`}
                      title="Ver trabajos realizados"
                      className="h-7 px-2 text-[11px] text-amber-300 hover:text-amber-200 bg-amber-500/10">
                      <Briefcase className="w-3 h-3 mr-1" /> Trabajos
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => { setEditExt(c); setFormExt({ ...EMPTY_EXT, ...c, nif: c.nif || "", telefono: c.telefono || "", email: c.email || "", zona_habitual: c.zona_habitual || "", especialidad: c.especialidad || "", tarifa_dia: c.tarifa_dia || "", forma_pago: c.forma_pago || "", dias_vencimiento: c.dias_vencimiento ?? "", notas: c.notas || "", fecha_nacimiento: c.fecha_nacimiento || "", cumple_show: c.cumple_show !== false }); setOpenExt(true); }}
                      data-testid={`edit-externo-${c.id}`}
                      className="h-7 w-7 p-0 text-zinc-400 hover:text-violet-300"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost"
                      onClick={async () => { if (window.confirm(`¿Eliminar ${c.nombre}?`)) { await api.delete(`/colaboradores/${c.id}`); load(); } }}
                      className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                    {(c.tipo_acceso === "sin_acceso" || expirado) && (
                      <Button size="sm" variant="ghost"
                        onClick={() => { setDiasAcceso(7); setConcederFor(c); }}
                        data-testid={`conceder-acceso-${c.id}`}
                        className="h-7 px-2 text-[11px] text-cyan-300 hover:text-cyan-200 bg-cyan-500/10">
                        <KeyRound className="w-3 h-3 mr-1" /> Conceder
                      </Button>
                    )}
                    {(c.tipo_acceso === "puntual" && !expirado) && (
                      <Button size="sm" variant="ghost"
                        onClick={() => revocarAcceso(c)}
                        data-testid={`revocar-acceso-${c.id}`}
                        className="h-7 px-2 text-[11px] text-red-300 hover:text-red-200 bg-red-500/10">
                        <ShieldOff className="w-3 h-3 mr-1" /> Revocar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {externos.length === 0 && <div className="text-xs text-zinc-500 italic text-center py-8">Aún no hay colaboradores. Pulsa "Nuevo".</div>}
        </div>
      </div>

      {/* Dialog Externo */}
      <Dialog open={openExt} onOpenChange={setOpenExt}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl">
          <DialogHeader><DialogTitle>{editExt ? "Editar colaborador" : "Nuevo colaborador externo"}</DialogTitle></DialogHeader>
          <form onSubmit={saveExt} className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-3">
              <label className="cursor-pointer group relative" data-testid="form-externo-foto-upload">
                {formExt.foto_url ? (
                  <img src={formExt.foto_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/40" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-zinc-800 border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-500 text-[10px] text-center px-1">Subir foto</div>
                )}
                <input type="file" accept="image/*" className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      toast.info("Subiendo foto...");
                      const { secure_url } = await uploadToCloudinary(file, "dnf/colaboradores/fotos", "image");
                      setFormExt({ ...formExt, foto_url: secure_url });
                      toast.success("Foto subida");
                    } catch (err) {
                      toast.error("Error: " + (err.message || err));
                    } finally {
                      e.target.value = "";
                    }
                  }} />
              </label>
              <div className="text-[11px] text-zinc-500">Foto del colaborador. Se mostrará en el reporte del cliente.</div>
            </div>
            <div className="col-span-2"><Label className="text-xs">Nombre *</Label>
              <Input required value={formExt.nombre} onChange={(e) => setFormExt({ ...formExt, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="form-externo-nombre" /></div>
            <div><Label className="text-xs">Teléfono</Label><Input value={formExt.telefono} onChange={(e) => setFormExt({ ...formExt, telefono: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={formExt.email} onChange={(e) => setFormExt({ ...formExt, email: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Zona habitual</Label><Input value={formExt.zona_habitual} onChange={(e) => setFormExt({ ...formExt, zona_habitual: e.target.value })} placeholder="Sevilla, Huelva…" className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Especialidad</Label><Input value={formExt.especialidad} onChange={(e) => setFormExt({ ...formExt, especialidad: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Tarifa/día (€)</Label><Input type="number" step="0.01" value={formExt.tarifa_dia} onChange={(e) => setFormExt({ ...formExt, tarifa_dia: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={formExt.autonomo} onChange={(e) => setFormExt({ ...formExt, autonomo: e.target.checked })} data-testid="form-externo-autonomo" /> <Label>Autónomo</Label></div>
            <div className="col-span-2 bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">Datos fiscales / bancarios</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">DNI / NIE / CIF</Label>
                  <Input value={formExt.nif} onChange={(e) => setFormExt({ ...formExt, nif: e.target.value.toUpperCase() })}
                    placeholder="12345678A o B12345678" className="bg-zinc-900 border-zinc-800 h-8 text-xs font-mono"
                    data-testid="form-externo-nif" />
                </div>
                <div>
                  <Label className="text-[10px]">IBAN</Label>
                  <Input value={formExt.iban || ""} onChange={(e) => setFormExt({ ...formExt, iban: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                    placeholder="ES00 0000 0000…" className="bg-zinc-900 border-zinc-800 h-8 text-xs font-mono" />
                </div>
                <div>
                  <Label className="text-[10px]">Nº Seguridad Social</Label>
                  <Input value={formExt.num_seguridad_social || ""} onChange={(e) => setFormExt({ ...formExt, num_seguridad_social: e.target.value.replace(/\s+/g, "") })}
                    placeholder="0000000000" className="bg-zinc-900 border-zinc-800 h-8 text-xs font-mono" />
                </div>
              </div>
              <div className="text-[10px] text-zinc-500">Sólo se rellena lo que aplique según si es autónomo o empleado.</div>
            </div>

            {/* 🎂 Cumpleaños */}
            <div className="col-span-2 bg-pink-500/5 border border-pink-500/30 rounded-lg p-3 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-pink-200">🎂 Fecha de nacimiento (opcional)</Label>
                <Input type="date" value={formExt.fecha_nacimiento || ""}
                  onChange={(e) => setFormExt({ ...formExt, fecha_nacimiento: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="form-externo-fecha-nacimiento" />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" id={`cs-${formExt.id || "new"}`} checked={!!formExt.cumple_show}
                  onChange={(e) => setFormExt({ ...formExt, cumple_show: e.target.checked })}
                  className="accent-pink-500 w-4 h-4" />
                <Label htmlFor={`cs-${formExt.id || "new"}`} className="text-xs cursor-pointer">
                  Visible — el equipo recibe aviso para felicitarle
                </Label>
              </div>
            </div>

            {formExt.autonomo && (
              <div className="col-span-2 bg-violet-500/5 border border-violet-700/40 rounded-lg p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold">Configuración autónomo</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">Forma de pago</Label>
                    <select value={formExt.forma_pago} onChange={(e) => setFormExt({ ...formExt, forma_pago: e.target.value })}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded h-8 text-xs px-2 text-zinc-100"
                      data-testid="form-externo-forma-pago">
                      <option value="">—</option>
                      {Object.entries(FORMAS_PAGO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select></div>
                  <div><Label className="text-[10px]">Días vencimiento</Label>
                    <Input type="number" min="0" max="365" value={formExt.dias_vencimiento}
                      onChange={(e) => setFormExt({ ...formExt, dias_vencimiento: e.target.value })}
                      placeholder="30 / 60 / 90" className="bg-zinc-900 border-zinc-800 h-8 text-xs"
                      data-testid="form-externo-dias-venc" /></div>
                </div>
                <div className="text-[10px] text-violet-300/70">
                  El vencimiento de cada factura se calcula automáticamente como <b>fecha de emisión + días vencimiento</b>. Avisaremos {DIAS_AVISO_VENCIMIENTO_LABEL} antes del vencimiento.
                </div>
              </div>
            )}
            <div className="col-span-2"><Label className="text-xs">Notas</Label><Input value={formExt.notas} onChange={(e) => setFormExt({ ...formExt, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div className="col-span-2 bg-zinc-900/40 border border-zinc-800 rounded-lg p-2.5">
              <Label className="text-xs mb-1 block">Tipo de acceso a la app</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {Object.entries(TIPOS_ACCESO).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setFormExt({ ...formExt, tipo_acceso: k })}
                    data-testid={`tipo-acceso-${k}`}
                    className={`text-xs py-2 rounded border transition-colors ${
                      formExt.tipo_acceso === k
                        ? "bg-violet-500/20 text-violet-200 border-violet-500/40"
                        : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                    }`}>{v.label}</button>
                ))}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">{TIPOS_ACCESO[formExt.tipo_acceso || "sin_acceso"].desc}</div>
            </div>

            {/* 🚗 Carnet de conducir */}
            <div className="col-span-2">
              <Label className="text-xs mb-1 block">🚗 Carnet de conducir</Label>
              <CarnetConducirForm
                value={formExt.carnet_conducir}
                onChange={(c) => setFormExt({ ...formExt, carnet_conducir: c })}
              />
            </div>

            <DialogFooter className="col-span-2">
              <Button type="button" variant="ghost" onClick={() => setOpenExt(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" className="bg-violet-500 hover:bg-violet-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Conceder acceso */}
      <Dialog open={!!concederFor} onOpenChange={(o) => !o && setConcederFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle>Conceder acceso a {concederFor?.nombre}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">Se generará un usuario con acceso durante el periodo indicado.</div>
            <div>
              <Label className="text-xs">Días de acceso</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {[3, 7, 14, 30].map((d) => (
                  <button key={d} type="button" onClick={() => setDiasAcceso(d)}
                    className={`text-xs py-2 rounded border ${
                      diasAcceso === d ? "bg-cyan-500/20 text-cyan-200 border-cyan-500/40" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                    }`}>{d}d</button>
                ))}
              </div>
              <Input type="number" min="1" max="365" value={diasAcceso}
                onChange={(e) => setDiasAcceso(parseInt(e.target.value) || 7)}
                className="bg-zinc-900 border-zinc-800 mt-2 text-sm" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConcederFor(null)} className="text-zinc-400">Cancelar</Button>
              <Button onClick={concederAcceso} data-testid="confirm-conceder-btn"
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-semibold">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> Generar credenciales
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credenciales generadas */}
      <Dialog open={!!credDialog} onOpenChange={(o) => !o && setCredDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle>🔑 Credenciales generadas</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-zinc-400">El acceso vence en <b className="text-amber-300">{credDialog?.dias} días</b>.</div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Email</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-cyan-300 text-sm">{credDialog?.email}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(credDialog?.email || ""); toast.success("Copiado"); }} className="h-6 w-6 p-0"><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Contraseña</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-amber-300 text-sm font-mono">{credDialog?.password}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(credDialog?.password || ""); toast.success("Copiado"); }} className="h-6 w-6 p-0"><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vencimiento</div>
                <div className="text-xs text-zinc-300">{credDialog?.acceso_hasta && new Date(credDialog.acceso_hasta).toLocaleString("es-ES")}</div>
              </div>
            </div>
            <Button onClick={() => {
              const txt = `Acceso RotulaciónDNF\n\nEmail: ${credDialog?.email}\nContraseña: ${credDialog?.password}\nVence: ${new Date(credDialog?.acceso_hasta).toLocaleString("es-ES")}`;
              navigator.clipboard.writeText(txt);
              toast.success("Mensaje completo copiado");
            }} variant="outline" className="w-full bg-transparent border-zinc-700 text-zinc-200">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar mensaje para WhatsApp
            </Button>
            <DialogFooter>
              <Button onClick={() => setCredDialog(null)} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Cerrar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal Trabajos del externo */}
      <ColaboradorTrabajosDialog
        colaborador={trabajosFor}
        onClose={() => setTrabajosFor(null)}
      />
    </div>
  );
}
